// TEMA VISUAL DE CADA BOLICHE
//
// La web pública está escrita contra las variables de lobbo.css (--red-*, --ink-*, --font-*).
// Hasta ahora esas variables eran fijas: todos los boliches se veían rojos. Acá se traducen
// los cuatro colores que el dueño elige en el panel a TODA esa paleta, para no tener que
// tocar cada pantalla.
//
// Si el boliche no eligió nada, esto devuelve {} y la web usa los valores de siempre: nadie
// ve un cambio que no pidió.

const HEX = /^#[0-9a-fA-F]{6}$/;

function aRGB(hex) {
  const h = String(hex || '').trim();
  if (!HEX.test(h)) return null;
  return [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
}

// Mezcla dos colores. p=0 devuelve el primero, p=1 el segundo.
function mezcla(hex, hacia, p) {
  const a = aRGB(hex), b = aRGB(hacia);
  if (!a || !b) return hex;
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * p));
  return '#' + c.map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase();
}

const NEGRO = '#000000';
const BLANCO = '#FFFFFF';

export const TIPOGRAFIAS = {
  default: { nombre: 'Por defecto', display: "'Unbounded','Arial Black',system-ui,sans-serif", body: "'DM Sans',system-ui,sans-serif", google: null },
  inter: { nombre: 'Inter', display: "'Inter',system-ui,sans-serif", body: "'Inter',system-ui,sans-serif", google: 'Inter:wght@400;500;600;700;800' },
  'dm-sans': { nombre: 'DM Sans', display: "'DM Sans',system-ui,sans-serif", body: "'DM Sans',system-ui,sans-serif", google: 'DM+Sans:wght@400;500;700' },
  poppins: { nombre: 'Poppins', display: "'Poppins',system-ui,sans-serif", body: "'Poppins',system-ui,sans-serif", google: 'Poppins:wght@400;500;600;700;800' },
  montserrat: { nombre: 'Montserrat', display: "'Montserrat',system-ui,sans-serif", body: "'Montserrat',system-ui,sans-serif", google: 'Montserrat:wght@400;500;600;700;800' },
  'space-grotesk': { nombre: 'Space Grotesk', display: "'Space Grotesk',system-ui,sans-serif", body: "'Space Grotesk',system-ui,sans-serif", google: 'Space+Grotesk:wght@400;500;600;700' },
  bebas: { nombre: 'Bebas Neue', display: "'Bebas Neue',Impact,sans-serif", body: "'DM Sans',system-ui,sans-serif", google: 'Bebas+Neue' },
};

// Temas listos para usar. Son solo un atajo: cargan los cuatro colores y la tipografía, y
// después el dueño puede tocar lo que quiera.
export const TEMAS = [
  // El `id` sigue siendo 'lobbo' a propósito: es el valor guardado en organizations.tema
  // de los boliches que ya lo eligieron. Cambiarlo les dejaría el tema sin marcar.
  { id: 'lobbo', nombre: 'RED', principal: '#C8102E', secundario: '#FFFFFF', fondo: '#000000', superficie: '#161616', tipografia: 'default' },
  { id: 'neon', nombre: 'Neón', principal: '#E1065A', secundario: '#FFFFFF', fondo: '#0A0A0A', superficie: '#1A1A1A', tipografia: 'poppins' },
  { id: 'minimal', nombre: 'Minimal', principal: '#111111', secundario: '#111111', fondo: '#FFFFFF', superficie: '#F2F2F2', tipografia: 'inter' },
  { id: 'techno', nombre: 'Techno', principal: '#00E5A0', secundario: '#FFFFFF', fondo: '#07110D', superficie: '#12211B', tipografia: 'space-grotesk' },
  { id: 'sunset', nombre: 'Sunset', principal: '#FF7A18', secundario: '#FFF6EC', fondo: '#140A04', superficie: '#241408', tipografia: 'montserrat' },
];

// Las variables CSS que hay que pisar, a partir de lo que eligió el boliche.
// ¿El dueño eligió un tema, o simplemente tiene datos viejos guardados?
//
// primary_color existe desde antes y varios boliches lo tienen cargado sin haberlo elegido
// nunca (nadie lo usaba). Si alcanzara con eso para pintar la web, al desplegar esto esos
// boliches cambiarían de color solos, sin tocar nada. Por eso hace falta una señal explícita:
// un tema predefinido, un color nuevo de la paleta, o una tipografía distinta de la de fábrica.
export function eligioTema(org) {
  if (!org) return false;
  return !!(org.tema || org.color_fondo || org.color_superficie || org.color_secundario
    || (org.tipografia && org.tipografia !== 'default'));
}

export function variablesTema(org) {
  if (!org || !eligioTema(org)) return {};
  const v = {};
  const principal = HEX.test(String(org.primary_color || '')) ? org.primary_color : null;
  const fondo = HEX.test(String(org.color_fondo || '')) ? org.color_fondo : null;
  const superficie = HEX.test(String(org.color_superficie || '')) ? org.color_superficie : null;
  const secundario = HEX.test(String(org.color_secundario || '')) ? org.color_secundario : null;

  if (principal) {
    // La escala de acento: de más oscuro a más claro, como la roja original.
    v['--red-900'] = mezcla(principal, NEGRO, 0.78);
    v['--red-800'] = mezcla(principal, NEGRO, 0.62);
    v['--red-700'] = mezcla(principal, NEGRO, 0.42);
    v['--red-600'] = mezcla(principal, NEGRO, 0.22);
    v['--red-500'] = mezcla(principal, NEGRO, 0.08);
    v['--red-400'] = principal;
    v['--red-300'] = mezcla(principal, BLANCO, 0.18);
    v['--accent'] = principal;
    const rgb = aRGB(principal).join(',');
    v['--glow-red'] = `0 0 0 1px rgba(${rgb},0.4),0 8px 40px -8px rgba(${rgb},0.6)`;
  }
  if (fondo) {
    v['--bg'] = fondo;
    v['--ink-000'] = fondo;
    v['--ink-050'] = mezcla(fondo, BLANCO, 0.03);
    v['--ink-100'] = mezcla(fondo, BLANCO, 0.06);
  }
  if (superficie) {
    v['--ink-200'] = superficie;
    v['--ink-300'] = mezcla(superficie, BLANCO, 0.05);
    v['--ink-400'] = mezcla(superficie, BLANCO, 0.12);
    v['--ink-500'] = mezcla(superficie, BLANCO, 0.22);
    v['--border'] = mezcla(superficie, BLANCO, 0.12);
  }
  if (secundario) {
    // El secundario es el color de los textos sobre el fondo.
    v['--white'] = secundario;
    v['--fg'] = secundario;
    v['--ink-900'] = secundario;
    v['--ink-800'] = mezcla(secundario, fondo || NEGRO, 0.22);
    v['--ink-700'] = mezcla(secundario, fondo || NEGRO, 0.45);
    v['--ink-600'] = mezcla(secundario, fondo || NEGRO, 0.62);
  }
  const t = TIPOGRAFIAS[org.tipografia];
  if (t && org.tipografia !== 'default') {
    v['--font-display'] = t.display;
    v['--font-body'] = t.body;
  }
  return v;
}

// Deja cargada la tipografía elegida. Devuelve una función para sacarla, así el que la usa
// no deja <link> colgados al cambiar de tema.
export function cargarFuente(tipografia) {
  const t = TIPOGRAFIAS[tipografia];
  if (!t || !t.google || typeof document === 'undefined') return () => {};
  const href = 'https://fonts.googleapis.com/css2?family=' + t.google + '&display=swap';
  if (document.querySelector(`link[href="${href}"]`)) return () => {};
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
  return () => { link.remove(); };
}
